/**
 * Quand j'ai le temps — ce qui n'a pas de date
 *
 * L'application est un calendrier : tout y est rangé par échéance. Restait
 * dehors ce qui n'en a pas et n'en aura jamais — détartrer la bouilloire,
 * trier le garage, rappeler l'assurance un jour. Ces tâches-là tombaient dans
 * la tranche « Sans date » de l'accueil, en bas, après tout ce qui est dû.
 *
 * Elles sont ici, triées par priorité plutôt que par date, parce que c'est la
 * seule question qui se pose quand on a une heure devant soi : par quoi
 * commencer ?
 *
 * L'écran Travaux fait déjà exactement cela, mais pour une seule catégorie. On
 * ne le duplique pas : celui-ci prend toutes les tâches sans échéance, Travaux
 * compris, et reste la vue d'ensemble. Travaux garde son budget et ses pièces.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { type Tache, aujourdhuiParis } from '@foyer/noyau';

import { CarteTache, Chargement, Erreur, Section, TRAIT, Vide } from '../composants/ui.js';
import { MiseEnFile } from '../lib/api.js';
import { useAppli, useRessource } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { FeuilleTache } from './FeuilleTache.js';

/** Du plus urgent au moins urgent, puis ce qui n'a rien dit. */
const TRANCHES = [
  { cle: 'haute', titre: 'En premier' },
  { cle: 'moyenne', titre: 'Quand ça vient' },
  { cle: 'basse', titre: 'Un jour' },
  { cle: '', titre: 'Sans priorité' },
] as const;

export function TempsLibre() {
  const { rafraichis, utilisateur } = useAppli();
  const aujourdhui = aujourdhuiParis();

  const taches = useRessource(() => donnees.taches({ statut: 'active' }));
  const foyer = useRessource(() => donnees.foyer());

  const [feuille, setFeuille] = useState<Tache | 'nouvelle' | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (taches.chargement && !taches.donnee) return <Chargement quoi="Chargement de la liste" />;
  if (taches.erreur && !taches.donnee) return <Erreur message={taches.erreur} />;

  const membres = foyer.donnee?.membres ?? [];
  const prenomDe = (id: string | null) =>
    id && id !== utilisateur?.id ? (membres.find((m) => m.id === id)?.prenom ?? null) : null;

  const sansDate = (taches.donnee?.taches ?? []).filter((t) => t.statut === 'active' && !t.echeance);

  async function coche(t: Tache) {
    setOccupe(t.id);
    setMessage(null);
    try {
      await donnees.coche(t.id, t.titre);
      rafraichis();
    } catch (err) {
      if (err instanceof MiseEnFile) {
        setMessage('Hors ligne : le cochage partira au retour du réseau.');
        rafraichis();
      } else {
        setMessage((err as Error).message);
      }
    } finally {
      setOccupe(null);
    }
  }

  return (
    <>
      <div className="entete-vue">
        <h1>À faire</h1>
      </div>
      <p className="chapeau">
        Ce qui n’a pas de date et n’en demande pas, rangé par priorité. À regarder quand on a une
        heure devant soi.
      </p>

      {message && <div className="note">{message}</div>}

      {sansDate.length === 0 ? (
        <Vide
          titre="Rien en attente."
          texte="Notez ici ce qui traîne sans urgence : détartrer la bouilloire, trier le garage, rappeler l’assurance. La priorité se règle en ouvrant la tâche."
        >
          <button type="button" className="bouton secondaire" onClick={() => setFeuille('nouvelle')}>
            Ajouter quelque chose
          </button>
        </Vide>
      ) : (
        TRANCHES.map(({ cle, titre }) => {
          const lot = sansDate.filter((t) => (t.priorite ?? '') === cle);
          if (lot.length === 0) return null;
          return (
            <Section key={cle || 'sans'} titre={titre} compte={lot.length}>
              <div className="liste-taches">
                {lot.map((t) => (
                  <CarteTache
                    key={t.id}
                    tache={t}
                    aujourdhui={aujourdhui}
                    assigneAutre={prenomDe(t.assigneA)}
                    occupe={occupe === t.id}
                    onCoche={() => void coche(t)}
                    onOuvre={() => setFeuille(t)}
                  />
                ))}
              </div>
            </Section>
          );
        })
      )}

      {/* Même bouton que l'accueil, et pour la même raison : une barre pleine
          largeur coûterait 84 px d'écran en permanence. */}
      <button
        type="button"
        className="ajout-rapide"
        aria-label="Ajouter une tâche sans date"
        onClick={() => setFeuille('nouvelle')}
      >
        <Plus strokeWidth={TRAIT} size={24} aria-hidden />
      </button>

      {feuille && (
        <FeuilleTache
          tache={feuille === 'nouvelle' ? undefined : feuille}
          // Ajoutée depuis cet écran, une tâche est sans date par défaut :
          // demander de décocher « Sans date » sur l'écran des tâches sans date
          // serait un comble.
          sansDateInitiale
          membres={membres}
          onFerme={() => setFeuille(null)}
        />
      )}
    </>
  );
}
